import { FC, useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { replaceDate } from "@/helper/dateHandling";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import styles from "@/components/actionChain/actionChains.module.css";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { actionChainProgressEntityAdapter } from "@/components/actionChain/actionChainProgressEntityAdapter";

const ActionChainActiveCompanyTable: FC = () => {
  const { t } = useTranslation();
  const [subjectName, setSubjectName] = useState<string>("");
  const baseHeading = t("actionchain_active_chains");
  usePageHeading(subjectName ? `${baseHeading} - ${subjectName}` : baseHeading);
  const { subjectId } = useParams() as {
    subjectId: string;
  };
  const navigate = useNavigate();

  const additionalUrlParameters: any = {};
  if (subjectId) {
    additionalUrlParameters["subject"] = subjectId;
  }

  const customRowActions = (row: any) => {
    return (
      <>
        <Tooltip
          arrow
          placement="top"
          enterDelay={1000}
          enterNextDelay={250}
          title={t("actionchain_show_progress")}
        >
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={() => handleNavigateToChainProgress(row.original)}
          >
            <VisibilityIcon />
          </GenericButton>
        </Tooltip>
      </>
    );
  };
  const columns = [
    ...getRecordMetadataColumns(t, ["id"]),
    {
      header: t("name"),
      id: "name",
      customColumnFilterId: "action_chain_template__name",
      accessorKey: "action_chain_template.name",
    },
    {
      header: t("status"),
      id: "status",
      accessorKey: "status",
    },
    {
      header: t("created_on"),
      accessorKey: "created_on",
      id: "created_on",
      filterVariant: "date",
      sortingFn: "datetime",
      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue())}</span>;
      },
    },
    {
      header: t("modified_on"),
      accessorKey: "modified_on",
      id: "modified_on",
      filterVariant: "date",
      sortingFn: "datetime",
      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue())}</span>;
      },
    },
    ...getRecordMetadataColumns(t, ["created_by", "modified_by"]),
  ];
  const handleNavigateBack = () => {
    navigate("/admin/companyList");
  };
  const handleNavigateToChainProgress = (row: any) => {
    navigate(
      `/actionchain/company/progress/${row?.action_chain_template?.id}/${row?.id}`
    );
  };

  const handleUpdateHeadingWithTableData = useCallback((data: any) => {
    if (!data || !data.results || !data.results[0]?.subject?.name) {
      return;
    }

    setSubjectName(data.results[0].subject.name);
  }, []);
  return (
    <>
      <Tooltip placement="right" title={t("back")}>
        <GenericButton
          variant="icon"
          color="iconBase"
          onClick={handleNavigateBack}
          className={styles.navigateBackButton}
        >
          <ArrowBackIcon />
        </GenericButton>
      </Tooltip>
      <GenericCrudTable
        entity="actionChainProgress"
        entityAdapter={actionChainProgressEntityAdapter}
        columns={columns}
        additionalUrlParameter={additionalUrlParameters}
        disableAddItem
        disallowDelete={true}
        customRowActions={customRowActions}
        onSuccessfulGetHandler={handleUpdateHeadingWithTableData}
        showColumnFilterbyDefault
      />
    </>
  );
};

export default ActionChainActiveCompanyTable;
